import React from 'react';
import { Box, ButtonBase, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { trakrefBlue } from '../../../color_utils';
import StatusChip from '../StatusChip';

// AB#17128 AC4: one tile per surface - count, status pills and the calc guide; disabled and data-dependency states hold the slot for later regulations
const SurfaceTile = ({ surface, counts, selected, onSelect, onInfo }) => {
    const disabled = Boolean(surface.disabledReason);
    const unavailable = Boolean(surface.dataDependency);
    const count = counts?.count;

    // Pills use the band names on every surface, matching the band filter chips
    const pills = surface.kind === 'inScope' || !counts ? [] : [
        { label: `${counts.hit.toLocaleString()} Hit`, tone: 'hit', show: counts.hit > 0 },
        { label: `${counts.near.toLocaleString()} Near`, tone: 'near', show: counts.near > 0 },
        { label: `${counts.low.toLocaleString()} Low`, tone: 'low', show: counts.low > 0 },
    ].filter((p) => p.show);

    return (
        <Box
            sx={{
                position: 'relative',
                border: `1px solid ${selected ? trakrefBlue : '#EBEDF8'}`,
                boxShadow: selected ? `inset 0 -3px 0 ${trakrefBlue}` : 'none',
                borderRadius: '8px',
                backgroundColor: disabled ? '#F7F8FC' : '#FFFFFF',
                minHeight: '132px',
            }}
        >
            <ButtonBase
                onClick={onSelect}
                disabled={disabled}
                aria-pressed={selected}
                aria-label={`${surface.name}${count !== undefined && !disabled ? `, ${count}` : ''}`}
                sx={{
                    display: 'block', width: '100%', height: '100%', textAlign: 'left', p: '14px 40px 14px 14px', borderRadius: '8px',
                    // Keyboard focus must be visible (N5)
                    '&.Mui-focusVisible': { outline: `2px solid ${trakrefBlue}`, outlineOffset: '2px' },
                }}
            >
                <Box sx={{ fontSize: '13px', fontWeight: 600, color: disabled ? 'text.disabled' : 'text.primary', minHeight: '36px' }}>
                    {surface.name}
                </Box>
                {disabled && (
                    <Box sx={{ fontSize: '12px', color: 'text.secondary', mt: '6px' }}>{surface.disabledReason}</Box>
                )}
                {!disabled && unavailable && (
                    <Box sx={{ fontSize: '12px', color: 'text.secondary', mt: '6px' }}>{surface.dataDependency}</Box>
                )}
                {!disabled && !unavailable && (
                    <>
                        <Box sx={{ fontSize: '28px', fontWeight: 700, color: 'text.primary', lineHeight: '36px', mt: '4px' }}>
                            {count === undefined ? '—' : count.toLocaleString()}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', mt: '6px' }}>
                            {pills.map((pill) => (
                                <StatusChip key={pill.tone} label={pill.label} tone={pill.tone} kind={surface.kind} />
                            ))}
                        </Box>
                    </>
                )}
            </ButtonBase>
            {!disabled && (
                // Single click opens the calc guide; no hover tooltip (17116 AC8)
                <IconButton
                    size="small"
                    onClick={onInfo}
                    aria-label={`How ${surface.name} is calculated`}
                    sx={{ position: 'absolute', top: 8, right: 6, ':focus': { outline: 'none' } }}
                >
                    <InfoOutlinedIcon fontSize="small" />
                </IconButton>
            )}
        </Box>
    );
};

export default SurfaceTile;
